import { Avatar, Box, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { FormatAlignLeft, Image, Phone, Title, TouchApp, Videocam } from "@mui/icons-material";
import React, {MouseEventHandler, ReactElement, useMemo, useRef, useState} from "react";
import { connect } from "react-redux";
import { ProjectDetails } from "../../../redux/types/firebase";
import {selectModeData} from "../../../redux/selectors/editor";
import {IGraphData, updateModeData, UpdateModeData} from "../../../redux/slices/editor";
import { Transition } from "../../../fstn/transition";
import { IScriptElement, ICharacterData, parseScript } from "../../script/parseScript";
import ScriptQuickView from "./ScriptQuickView";
import Transitions from "./Transitions";
import NodeHeader from "./NodeHeader";
import {IStateMeta} from "../../../fstn/state";
import {getStyles} from "../../../utility/Style";
import {selectConstants} from "../../../redux/selectors/firebase";
import {Constants, Tags} from "../StateTags";

const useStyles = makeStyles(theme => ({
    avatar: {
        height: theme.spacing(3),
        width: theme.spacing(3)
    },
    script: {
        display: "-webkit-box",
        "-webkit-line-clamp": 8,
        "-webkit-box-orient": "vertical",
        overflow: "hidden",
        margin: theme.spacing(1),
        wordBreak: "break-word",
        minHeight: "60px"
    }
}));

interface IProps
{
    constants: Constants
    id: string
    data: {
        child?: string
        initial: string
        script: string
        label: string
        meta: IStateMeta
        goto?: { label: string, destination: string }[]
        transitions: Transition[]
        onTransitionDelete: (source: string, target: string | undefined, trigger: string | undefined) => void
    }
    onClickGoto: (id: string) => void
    onContextMenu: (event: React.MouseEvent<HTMLElement>) => void
    graphData: IGraphData
    projectDetails: ProjectDetails
    setShowTitle: (show: boolean) => void
    tags: Tags
    updateModeData: UpdateModeData
}

function ScriptLayout({constants, id, data, onClickGoto, onContextMenu, graphData, projectDetails, setShowTitle,
                          tags, updateModeData}: IProps)
{
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const containerEl = useRef<HTMLDivElement>(null);
    const scriptEl = useRef<HTMLParagraphElement>(null);
    const handlePointerEnter = ({buttons, currentTarget}: React.MouseEvent<HTMLElement>) =>
    {
        if (buttons === 0 && graphData.quickViewEnabled && graphData.quickViewState !== id &&
            graphData.quickViewEdit === undefined)
        {
            setAnchorEl(currentTarget);
            setShowTitle(false);
            updateModeData({quickViewState: id});
        }
    }
    const handleToolbarClick: MouseEventHandler<HTMLDivElement> = (event) => {
        if (! graphData.selected.includes(id)) {
            if (event.ctrlKey) {
                updateModeData({
                    selected: graphData.selected.concat([id])
                        .sort((rhs, lhs) => lhs.localeCompare(rhs)),
                    selectedActive: id
                });
            } else {
                updateModeData({
                    selected: [id],
                    selectedActive: id
                });
            }
        } else if (event.ctrlKey) {
            const selected = graphData.selected.filter(selected => selected !== id);
            const selectedActive = graphData.selectedActive === id ? (selected.length ? selected[0]: "") :
                graphData.selectedActive

            updateModeData({
                selected,
                selectedActive
            });
        } else {
            updateModeData({
                selectedActive: id
            });
        }
    };

    const icons: { [key: string]: (element: IScriptElement, key: string, index: number) => ReactElement | null } = {
        character: (element, key, index) =>
        {
            const data = element.data as ICharacterData;
            const image = `${data.character.toLowerCase()}.png`;
            const src = projectDetails.imageSource;
            const icons: { [key: string]: ReactElement } = {
                phone: <Phone fontSize="small" />,
                voicemail: <Phone fontSize="small" />
            };
            const icon = icons[data.character.toLowerCase()];


            return icon ? <Avatar key={`${key}_${index}`} className={classes.avatar} > {icon}</Avatar>
                : <Avatar key={`${key}_${index}`} className={classes.avatar} src={`${src}/intertitle/characters/${image}`} />
        },
        paragraph: () => <FormatAlignLeft />,
        cut: (_element, key, index) => <Videocam key={`${key}_${index}`} />,
        gesture: (_element, key, index) => <TouchApp key={`${key}_${index}`} />,
        line: () => null,
        title: (_element, key, index) => <Title key={`${key}_${index}`} />,
        note: () => null,
        option: (_element, key, index) => <TouchApp key={`${key}_${index}`} />,
        still: (_element, key, index) => <Image key={`${key}_${index}`} />
    };

    const iconPreference = [
        "character",
        "option",
        "gesture",
        "title",
        "still",
        "cut",
    ].reverse();

    const getIcon = (element: IScriptElement, index: number) =>
    {
        return element.type in icons ? icons[element.type](element, id, index) : null;
    }

    const iconOrder = (a: IScriptElement, b: IScriptElement) =>
    {
        return iconPreference.indexOf(b.type) - iconPreference.indexOf(a.type)
    }

    const parsed = parseScript(data.script)

    const icon = data.script.length > 0 ?
        parsed.slice().sort(iconOrder).map(getIcon).filter((element) => element !== null)[0]
        : null;
    const headerStyle = useMemo(() =>
        getStyles(id, tags, data.meta.blockNames || [], constants, "header"),
        [constants, data.meta.blockNames, id, tags]);
    const bodyStyle = useMemo(() =>
        getStyles(id, tags, data.meta.blockNames || [], constants, "body"),
        [constants, data.meta.blockNames, id, tags]);
    const footerStyle = useMemo(() =>
        getStyles(id, tags, data.meta.blockNames || [], constants, "footer"),
        [constants, data.meta.blockNames, id, tags]);

    return (
        <div ref={containerEl}>
            <Box className={headerStyle}>
                <NodeHeader data={data} onContextMenu={onContextMenu} onToolbarClick={handleToolbarClick} id={id}
                    icon={icon} projectDetails={projectDetails} />
            </Box> 
            {data.script.length > 0 &&
                <Box className={bodyStyle} onPointerOver={handlePointerEnter} >
                    <Typography ref={scriptEl} className={classes.script}>{data.script}</Typography>
                </Box>
            }
    
            <Box className={footerStyle}>
                <Transitions transitions={data.transitions} id={id} onJump={onClickGoto}/>
            </Box>
    
            {graphData.quickViewEnabled && graphData.quickViewState === id && <ScriptQuickView
                data={data}
                id={id}
                icon={icon}
                anchorEl={anchorEl}
                containerEl={containerEl}
                setAnchorEl={setAnchorEl}
                setShowTitle={setShowTitle}
                content={parsed}
                projectDetails={projectDetails}
            />}
        </div>
    );
}

const mapStateToProps = (store: never) => ({
    constants: selectConstants(store),
    graphData: selectModeData(store) as IGraphData
});

export default connect(mapStateToProps, {
    updateModeData
})(ScriptLayout);